import React, { useEffect, useContext, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ALL_ROUTES } from 'config/routes';
import useFetchList from 'hooks/useFetchList';
import { AppRouteContext } from 'contexts';
import { USERS_COLUMNS } from '../constants';
import QhsTable from '../../Shared/Table/Table';
import { convertDataFromApi, useGenerateColumns } from '../../Shared/Table/helpers';
import RowAction from './widgets/RowAction';
import RemoveRowDialog from './widgets/RemoveRowDialog';
import { useSelector } from 'react-redux';
import TableTitle from '../../Shared/Table/widgets/TableTitle';
import TablePageContainer from '../../Shared/Table/widgets/TablePageContainer';
import AddButton from '../../Shared/Table/widgets/AddButton';
import { TABLE_KEYS } from '../../../redux_store/reducer/reducers/tableReducer/tableReducer';

const UsersList = () => {
  const { accountUuid } = useParams();
  const { en: { titles } } = useSelector((state) => state.i18n);
  const [isOpenRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [removingUser, setRemovingUser] = useState({});
  const { setAppRoute, breadcrumbs } = useContext(AppRouteContext);
  const {
    metadata: { currentAccount },
    isDistributionSponsor,
  } = useSelector((state) => state.user);
  const tableKey = isDistributionSponsor ? TABLE_KEYS.usersDSTable : TABLE_KEYS.usersTable;
  const columns = useGenerateColumns(USERS_COLUMNS, tableKey, {}, {}).columns;
  const fields = columns.map((columnData) => columnData.accessorKey).join();


  const {
    items,
    totalCount,
    reloadData,
    loading: dataLoading,
    onLoadMoreRows,
    sort,
    onSort,
    filter,
    onFilter,
  } = useFetchList({
    baseEndpoint: `/accounts/${accountUuid || currentAccount.uuid}/users`,
    baseParams: { fields: fields },
    tableKey,
    canLoad: fields.length,
  });

  const [prevTotalCount, setPrevTotalCount] = useState(totalCount);

  useEffect(() => {
    if (prevTotalCount !== totalCount) {
      setPrevTotalCount(totalCount);
    }
  }, [filter, totalCount]);

  const handleRowClick = ({ uuid, ...args }) => {
    const isItChildAccount = accountUuid
      && accountUuid !== currentAccount.uuid
      && isDistributionSponsor;
    setAppRoute({
      path: (isItChildAccount ? ALL_ROUTES.user.editDS : ALL_ROUTES.user.edit).path,
      params: {
        currentAccountUuid: currentAccount.uuid,
        accountUuid: isItChildAccount ? accountUuid : null,
        userUuid: uuid,
      },
      title: titles.editUser,
    });
  };

  const handleUserCreate = () => {
    const isItChildAccount = accountUuid && isDistributionSponsor;
    setAppRoute({
      title: titles.createUser,
      path: (isItChildAccount ? ALL_ROUTES.user.createDS : ALL_ROUTES.user.create).path,
      params: {
        currentAccountUuid: currentAccount.uuid,
        accountUuid: isItChildAccount ? accountUuid : null,
      },
    });
  };

  const users = useMemo(
    () => items && convertDataFromApi(items, tableKey, filter),
    [items],
  );

  const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
  const titleText = lastBreadcrumb ? lastBreadcrumb.title : titles.users;

  const hasFilters = useMemo(
    () => !!filter?.reduce((accumulator, { value }) => {
      return accumulator + value.toString().replaceAll(' ', '').length;
    }, 0),
    [filter],
  );

  return (
    <TablePageContainer>
      <QhsTable
        columns={columns}
        data={users || []}
        fetchNextPage={onLoadMoreRows}
        isFetching={dataLoading}
        isLoading={dataLoading && !users}
        totalRowCount={totalCount}
        sorting={sort}
        setSorting={onSort}
        filter={filter}
        setFilters={onFilter}
        tableKey={tableKey}
        onRowClick={handleRowClick}
        tableTitle={<TableTitle
          titleText={titleText}
          count={totalCount || totalCount === 0 ? totalCount : null}
          countFiltered={hasFilters}
        />}
        menuActions={<AddButton label={titles.user} onClick={handleUserCreate} />}
        renderRowActionMenuItems={({ row, setAnchorEl }) => <RowAction
          row={row}
          setAnchorEl={setAnchorEl}
          setOpenRemoveDialog={setOpenRemoveDialog}
          setRemovingUser={setRemovingUser}
        />}
      />
      <RemoveRowDialog
        removingUser={removingUser}
        isOpenRemoveDialog={isOpenRemoveDialog}
        setOpenRemoveDialog={setOpenRemoveDialog}
        setRemovingUser={setRemovingUser}
        reloadData={reloadData}
      />
    </TablePageContainer>
  );
};

export default UsersList;
